import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import Draggabilly from 'draggabilly';
import Packery from 'packery';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { resizeImageToDataUrl } from '../utils/image';
import { renderMarkdown } from '../utils/markdown';
import { FiCalendar, FiSettings, FiLogOut, FiBell, FiUser, FiClock, FiBellOff, FiPlus, FiMessageSquare, FiImage, FiX, FiLink, FiEdit, FiCheckSquare, FiChevronDown, FiChevronRight, FiFolder, FiEye, FiEyeOff, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import LoadingSpinner from '../components/LoadingSpinner';
import TasksDrawer from '../components/TasksDrawer';
import { QRCodeCanvas } from 'qrcode.react';
import toast from 'react-hot-toast';

const getBookmarkCacheKey = (userId) => `futurenote.bookmarks.cache.${userId}`;
const getBookmarkLayoutKey = (userId) => `futurenote.bookmarks.layout.${userId}`;
const getTasksDrawerLayoutKey = (userId) => `futurenote.tasks.drawer.layout.${userId}`;
const DEFAULT_DASHBOARD_WIDGET_ORDER = [
  'welcome',
  'upcoming_events',
  'quick_actions',
  'calendar_connections',
  'notification_status',
  'chats',
  'bookmarks',
  'future_notes'
];

const readLocalJson = (key, fallback) => {
  if (typeof window === 'undefined' || !key) {
    return fallback;
  }

  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (error) {
    return fallback;
  }
};

const writeLocalJson = (key, value) => {
  if (typeof window === 'undefined' || !key) {
    return;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Ignore local storage write failures.
  }
};

const arraysEqual = (left = [], right = []) => (
  left.length === right.length && left.every((value, index) => value === right[index])
);

const mergeStoredOrder = (existingOrder = [], currentIds = []) => {
  const currentSet = new Set(currentIds);
  const merged = existingOrder.filter((id) => currentSet.has(id));

  for (const id of currentIds) {
    if (!merged.includes(id)) {
      merged.push(id);
    }
  }

  return merged;
};

const reorderArray = (items = [], fromId, toId) => {
  const next = [...items];
  const fromIndex = next.indexOf(fromId);
  const toIndex = next.indexOf(toId);

  if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
    return next;
  }

  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

const mergeVisibleOrderIntoFullOrder = (existingOrder = [], visibleOrder = [], allIds = []) => {
  const normalizedAllIds = mergeStoredOrder(existingOrder, allIds);
  const visibleSet = new Set(visibleOrder);
  const next = [];
  let nextVisibleIndex = 0;

  for (const id of normalizedAllIds) {
    if (visibleSet.has(id)) {
      next.push(visibleOrder[nextVisibleIndex] || id);
      nextVisibleIndex += 1;
    } else {
      next.push(id);
    }
  }

  return next;
};

const normalizeDashboardWidgetOrder = (order = []) => {
  const nextOrder = [];
  const seen = new Set();

  for (const widgetId of order) {
    if (DEFAULT_DASHBOARD_WIDGET_ORDER.includes(widgetId) && !seen.has(widgetId)) {
      nextOrder.push(widgetId);
      seen.add(widgetId);
    }
  }

  for (const widgetId of DEFAULT_DASHBOARD_WIDGET_ORDER) {
    if (!seen.has(widgetId)) {
      nextOrder.push(widgetId);
    }
  }

  return nextOrder;
};

const normalizeHiddenWidgets = (hiddenWidgets = []) => {
  const seen = new Set();
  const nextHidden = [];

  for (const widgetId of hiddenWidgets) {
    if (DEFAULT_DASHBOARD_WIDGET_ORDER.includes(widgetId) && !seen.has(widgetId)) {
      nextHidden.push(widgetId);
      seen.add(widgetId);
    }
  }

  return nextHidden;
};

const getBookmarkIconUrl = (url) => {
  try {
    const parsed = new URL(url);
    return `${parsed.origin}/favicon.ico`;
  } catch (error) {
    return '';
  }
};

const getBookmarkDisplayIcon = (bookmark) => (
  typeof bookmark?.iconData === 'string' && bookmark.iconData.trim()
    ? bookmark.iconData.trim()
    : getBookmarkIconUrl(bookmark?.url)
);

const getBookmarkInitials = (title = '', url = '') => {
  const base = title.trim() || url.trim();
  if (!base) {
    return 'BM';
  }

  return base
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('') || 'BM';
};

const BOOKMARK_TILE_MIN_WIDTH = 280;
const BOOKMARK_GRID_ROW_HEIGHT = 12;
const BOOKMARK_GRID_GAP = 6;
const BOOKMARK_GRID_CELL_SIZE = BOOKMARK_TILE_MIN_WIDTH;
const BOOKMARK_PACKERY_GAP = 12;

const getBookmarkFolderSegments = (folderPath = '') => {
  const segments = folderPath
    .split(/\s*\/\s*/)
    .map((segment) => segment.trim())
    .filter(Boolean);

  return segments.length > 0 ? segments : ['Unsorted'];
};

const getBookmarkFolderLabel = (folderPath = '') => {
  const segments = getBookmarkFolderSegments(folderPath);
  return segments[segments.length - 1] || 'Unsorted';
};

const normalizeBookmarkDefaultOpenMode = (value) => (
  value === 'same_tab' ? 'same_tab' : 'new_tab'
);

const normalizeBookmarkOverrideOpenMode = (value) => (
  value === 'same_tab' || value === 'new_tab' ? value : 'inherit'
);

const getEffectiveBookmarkOpenMode = (bookmark, folderOverrideMap, defaultOpenMode) => {
  const bookmarkOpenMode = normalizeBookmarkOverrideOpenMode(bookmark?.openMode);
  if (bookmarkOpenMode !== 'inherit') {
    return bookmarkOpenMode;
  }

  const folderOpenMode = normalizeBookmarkOverrideOpenMode(folderOverrideMap.get(bookmark?.folderPath || 'Unsorted'));
  if (folderOpenMode !== 'inherit') {
    return folderOpenMode;
  }

  return normalizeBookmarkDefaultOpenMode(defaultOpenMode);
};

const getBookmarkTileMeta = (itemCount, isCollapsed) => {
  const safeCount = Math.max(1, itemCount || 0);
  const previewCount = isCollapsed ? Math.min(safeCount, 6) : safeCount;
  const columns = isCollapsed
    ? Math.min(3, Math.max(2, Math.ceil(Math.sqrt(Math.min(previewCount, 6)))))
    : Math.min(4, Math.max(2, Math.ceil(Math.sqrt(Math.min(previewCount, 16)))));
  const bookmarkRows = Math.max(1, Math.ceil(previewCount / columns));
  const headerHeight = isCollapsed ? 44 : 56;
  const bookmarkRowHeight = isCollapsed ? 48 : 84;
  const footerHeight = safeCount > previewCount ? 24 : 0;
  const tileHeight = headerHeight + (bookmarkRows * bookmarkRowHeight) + footerHeight + 24;

  return {
    columns,
    previewCount,
    rowSpan: Math.max(1, Math.ceil((tileHeight + BOOKMARK_GRID_GAP) / (BOOKMARK_GRID_ROW_HEIGHT + BOOKMARK_GRID_GAP))),
    tileHeight
  };
};

const Dashboard = () => {
  const { user, logout, updateUser } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const userId = user?.id || '';
  const [hoveredEventId, setHoveredEventId] = React.useState(null);
  const [isCreateChatOpen, setIsCreateChatOpen] = React.useState(false);
  const [isTasksDrawerOpen, setIsTasksDrawerOpen] = React.useState(false);
  const [isTasksDrawerPinned, setIsTasksDrawerPinned] = React.useState(false);
  const [chatTitle, setChatTitle] = React.useState('');
  const [chatBackground, setChatBackground] = React.useState('');
  const [isProcessingBackground, setIsProcessingBackground] = React.useState(false);
  const [qrChat, setQrChat] = React.useState(null);
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const [noteContent, setNoteContent] = React.useState('');
  const [notePublishAt, setNotePublishAt] = React.useState('');
  const [dashboardWidgetOrder, setDashboardWidgetOrder] = React.useState(DEFAULT_DASHBOARD_WIDGET_ORDER);
  const [hiddenDashboardWidgets, setHiddenDashboardWidgets] = React.useState([]);
  const [bookmarkCache, setBookmarkCache] = React.useState(null);
  const [bookmarkFolderOrder, setBookmarkFolderOrder] = React.useState([]);
  const [bookmarkItemOrder, setBookmarkItemOrder] = React.useState({});
  const [collapsedBookmarkFolders, setCollapsedBookmarkFolders] = React.useState({});

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('open') !== 'settings') {
      return;
    }

    navigate(`/settings?${params.toString()}`, { replace: true });
  }, [location.search, navigate]);
  const [hiddenBookmarkFolders, setHiddenBookmarkFolders] = React.useState({});
  const [bookmarkDrag, setBookmarkDrag] = React.useState(null);
  const [bookmarkIconErrors, setBookmarkIconErrors] = React.useState({});
  const [bookmarkPackeryColumns, setBookmarkPackeryColumns] = React.useState(3);
  const bookmarkCacheKey = React.useMemo(() => (userId ? getBookmarkCacheKey(userId) : ''), [userId]);
  const bookmarkLayoutKey = React.useMemo(() => (userId ? getBookmarkLayoutKey(userId) : ''), [userId]);
  const tasksDrawerLayoutKey = React.useMemo(() => (userId ? getTasksDrawerLayoutKey(userId) : ''), [userId]);
  const bookmarkPackeryRef = React.useRef(null);
  const bookmarkPackeryInstanceRef = React.useRef(null);
  const bookmarkPackeryDraggiesRef = React.useRef([]);

  // Fetch upcoming events
  const { data: events, isLoading: eventsLoading } = useQuery(
    'upcomingEvents',
    async () => {
      const response = await api.get('/calendar/events/upcoming?limit=5');
      return response.data.events;
    },
    {
      refetchInterval: 30000, // Refetch every 30 seconds
    }
  );

  // Fetch calendar status
  const { data: calendarStatus, isLoading: statusLoading } = useQuery(
    'calendarStatus',
    async () => {
      const response = await api.get('/calendar/status');
      return response.data;
    }
  );

  const toggleNotificationsMutation = useMutation(
    async ({ id, enabled }) => {
      const response = await api.put(`/calendar/events/${id}/notifications`, { enabled });
      return response.data.event;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('upcomingEvents');
      },
    }
  );

  const updateNotificationTimeMutation = useMutation(
    async ({ id, notificationTime }) => {
      const response = await api.put(`/calendar/events/${id}/notifications`, { notificationTime });
      return response.data.event;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('upcomingEvents');
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Failed to update notification time';
        toast.error(message);
      }
    }
  );

  const { data: chats = [], isLoading: chatsLoading } = useQuery(
    'chats',
    async () => {
      const response = await api.get('/chats');
      return response.data.chats || [];
    }
  );

  const { data: bookmarkData, isLoading: bookmarksLoading } = useQuery(
    'bookmarks',
    async () => {
      const response = await api.get('/bookmarks?limit=120');
      return response.data;
    }
  );

  React.useEffect(() => {
    if (!userId) {
      setBookmarkCache(null);
      setBookmarkFolderOrder([]);
      setBookmarkItemOrder({});
      setCollapsedBookmarkFolders({});
      setHiddenBookmarkFolders({});
      setBookmarkDrag(null);
      setBookmarkIconErrors({});
      return;
    }

    setBookmarkCache(readLocalJson(bookmarkCacheKey, null));
    const storedLayout = readLocalJson(bookmarkLayoutKey, null);
    setBookmarkFolderOrder(Array.isArray(storedLayout?.folderOrder) ? storedLayout.folderOrder : []);
    setBookmarkItemOrder(storedLayout?.itemOrder && typeof storedLayout.itemOrder === 'object' ? storedLayout.itemOrder : {});
    setCollapsedBookmarkFolders(
      storedLayout?.collapsedFolders && typeof storedLayout.collapsedFolders === 'object'
        ? storedLayout.collapsedFolders
        : {}
    );
    setHiddenBookmarkFolders(
      storedLayout?.hiddenFolders && typeof storedLayout.hiddenFolders === 'object'
        ? storedLayout.hiddenFolders
        : {}
    );
    setBookmarkIconErrors({});
  }, [bookmarkCacheKey, bookmarkLayoutKey, userId]);

  React.useEffect(() => {
    if (!tasksDrawerLayoutKey) {
      setIsTasksDrawerPinned(false);
      setIsTasksDrawerOpen(false);
      return;
    }

    const storedLayout = readLocalJson(tasksDrawerLayoutKey, null);
    const pinned = !!storedLayout?.pinned;
    setIsTasksDrawerPinned(pinned);
    setIsTasksDrawerOpen(pinned);
  }, [tasksDrawerLayoutKey]);

  React.useEffect(() => {
    if (!tasksDrawerLayoutKey) {
      return;
    }

    writeLocalJson(tasksDrawerLayoutKey, {
      pinned: isTasksDrawerPinned
    });
  }, [isTasksDrawerPinned, tasksDrawerLayoutKey]);

  React.useEffect(() => {
    const nextOrder = normalizeDashboardWidgetOrder(user?.dashboardPreferences?.widgetOrder || DEFAULT_DASHBOARD_WIDGET_ORDER);
    const nextHidden = normalizeHiddenWidgets(user?.dashboardPreferences?.hiddenWidgets || []);
    setDashboardWidgetOrder((prev) => (arraysEqual(prev, nextOrder) ? prev : nextOrder));
    setHiddenDashboardWidgets((prev) => (arraysEqual(prev, nextHidden) ? prev : nextHidden));
  }, [user?.dashboardPreferences]);

  React.useEffect(() => {
    if (!bookmarkData || !bookmarkCacheKey) {
      return;
    }

    setBookmarkCache(bookmarkData);
    writeLocalJson(bookmarkCacheKey, bookmarkData);
  }, [bookmarkData, bookmarkCacheKey]);

  React.useEffect(() => {
    if (!bookmarkLayoutKey || !userId) {
      return;
    }

    writeLocalJson(bookmarkLayoutKey, {
      folderOrder: bookmarkFolderOrder,
      itemOrder: bookmarkItemOrder,
      collapsedFolders: collapsedBookmarkFolders,
      hiddenFolders: hiddenBookmarkFolders
    });
  }, [bookmarkFolderOrder, bookmarkItemOrder, collapsedBookmarkFolders, hiddenBookmarkFolders, bookmarkLayoutKey, userId]);

  const createChatMutation = useMutation(
    async (payload) => {
      const response = await api.post('/chats', payload);
      return response.data.chat;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('chats');
        setIsCreateChatOpen(false);
        setChatTitle('');
        setChatBackground('');
      },
      onError: (error) => {
        const message = error.response?.data?.error || 'Failed to create chat';
        toast.error(message);
      }
    }
  );

  const { data: notes = [], isLoading: notesLoading } = useQuery(
    'notes',
    async () => {
      const response = await api.get('/notes?published=true&limit=30');
      return response.data.notes || [];
    }
  );

  const [expandedNoteId, setExpandedNoteId] = React.useState(null);
  const [commentDrafts, setCommentDrafts] = React.useState({});

  const { data: noteComments = [], isLoading: commentsLoading } = useQuery(
    ['noteComments', expandedNoteId],
    async () => {
      const response = await api.get(`/notes/${expandedNoteId}/comments?limit=30`);
      return response.data.comments || [];
    },
    {
      enabled: !!expandedNoteId
    }
  );

  const reactionMutation = useMutation(
    async ({ noteId, type }) => {
      const response = await api.post(`/notes/${noteId}/reaction`, { type });
      return { noteId, ...response.data };
    },
    {
      onSuccess: ({ noteId, reaction, likesCount, dislikesCount }) => {
        queryClient.setQueryData('notes', (prev = []) => {
          if (!Array.isArray(prev)) {
            return prev;
          }
          return prev.map((note) => (
            note.id === noteId
              ? {
                  ...note,
                  userReaction: reaction,
                  likesCount,
                  dislikesCount
                }
              : note
          ));
        });
      },
      onError: (error) => {
        const message = error.response?.data?.error || 'Failed to update reaction';
        toast.error(message);
      }
    }
  );

  const commentMutation = useMutation(
    async ({ noteId, content }) => {
      const response = await api.post(`/notes/${noteId}/comments`, { content });
      return { noteId, comment: response.data.comment };
    },
    {
      onSuccess: ({ noteId, comment }) => {
        queryClient.setQueryData(['noteComments', noteId], (prev = []) => {
          if (!Array.isArray(prev)) {
            return prev;
          }
          return [comment, ...prev];
        });
        queryClient.setQueryData('notes', (prev = []) => {
          if (!Array.isArray(prev)) {
            return prev;
          }
          return prev.map((note) => (
            note.id === noteId
              ? { ...note, commentsCount: (note.commentsCount || 0) + 1 }
              : note
          ));
        });
        setCommentDrafts((prev) => ({ ...prev, [noteId]: '' }));
      },
      onError: (error) => {
        const message = error.response?.data?.error || 'Failed to add comment';
        toast.error(message);
      }
    }
  );

  const createNoteMutation = useMutation(
    async (payload) => {
      const response = await api.post('/notes', payload);
      return response.data.note;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notes');
        setNoteContent('');
        setNotePublishAt('');
        toast.success('FutureNote scheduled');
      },
      onError: (error) => {
        const message = error.response?.data?.error || 'Failed to publish note';
        toast.error(message);
      }
    }
  );

  const dashboardPreferencesMutation = useMutation(
    async ({ widgetOrder, hiddenWidgets }) => {
      const response = await api.put('/users/dashboard-preferences', {
        widgetOrder,
        hiddenWidgets
      });
      return response.data.user;
    },
    {
      onSuccess: (nextUser) => {
        updateUser(nextUser);
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Failed to save dashboard layout';
        toast.error(message);
      }
    }
  );

  const handleLogout = () => {
    logout();
  };

  const handleOpenTasksDrawer = () => {
    setIsTasksDrawerOpen(true);
  };

  const handleCloseTasksDrawer = () => {
    setIsTasksDrawerPinned(false);
    setIsTasksDrawerOpen(false);
  };

  const handleTasksDrawerPinnedChange = (nextPinned) => {
    setIsTasksDrawerPinned(nextPinned);
    if (nextPinned) {
      setIsTasksDrawerOpen(true);
    }
  };

  const persistDashboardPreferences = React.useCallback((nextOrder, nextHidden) => {
    dashboardPreferencesMutation.mutate({
      widgetOrder: nextOrder,
      hiddenWidgets: nextHidden
    });
  }, [dashboardPreferencesMutation]);

  const handleMoveWidget = (widgetId, direction) => {
    const currentIndex = dashboardWidgetOrder.indexOf(widgetId);
    if (currentIndex === -1) {
      return;
    }

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= dashboardWidgetOrder.length) {
      return;
    }

    const nextOrder = [...dashboardWidgetOrder];
    const [movedWidget] = nextOrder.splice(currentIndex, 1);
    nextOrder.splice(targetIndex, 0, movedWidget);
    setDashboardWidgetOrder(nextOrder);
    persistDashboardPreferences(nextOrder, hiddenDashboardWidgets);
  };

  const handleHideWidget = (widgetId) => {
    if (hiddenDashboardWidgets.includes(widgetId)) {
      return;
    }

    const nextHidden = [...hiddenDashboardWidgets, widgetId];
    setHiddenDashboardWidgets(nextHidden);
    persistDashboardPreferences(dashboardWidgetOrder, nextHidden);
  };

  const handleToggleNotifications = (event) => {
    const currentlyEnabled = event?.notificationSettings?.enabled !== false;
    toggleNotificationsMutation.mutate({ id: event.id, enabled: !currentlyEnabled });
  };

  const handleBackgroundSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    try {
      setIsProcessingBackground(true);
      const dataUrl = await resizeImageToDataUrl(file, { maxDimension: 1600, quality: 0.8 });
      setChatBackground(dataUrl);
    } catch (error) {
      toast.error('Failed to process image');
    } finally {
      setIsProcessingBackground(false);
    }
  };

  const handleCreateChat = () => {
    if (!chatTitle.trim()) {
      toast.error('Please enter a chat title');
      return;
    }
    createChatMutation.mutate({
      title: chatTitle.trim(),
      backgroundImage: chatBackground || ''
    });
  };

  const handleCreateNote = () => {
    if (!noteContent.trim()) {
      toast.error('Please write a note');
      return;
    }
    if (!notePublishAt) {
      toast.error('Select a publish date and time');
      return;
    }
    createNoteMutation.mutate({
      content: noteContent.trim(),
      publishAt: new Date(notePublishAt).toISOString()
    });
  };

  const activeBookmarkData = bookmarkData || bookmarkCache;
  const bookmarkDefaultOpenMode = React.useMemo(
    () => normalizeBookmarkDefaultOpenMode(user?.dashboardPreferences?.bookmarks?.defaultOpenMode),
    [user?.dashboardPreferences]
  );
  const bookmarkFolderOverrideMap = React.useMemo(
    () => new Map(
      (user?.dashboardPreferences?.bookmarks?.folderOverrides || [])
        .map((override) => ({
          folderPath: typeof override?.folderPath === 'string' ? override.folderPath.trim() : '',
          openMode: normalizeBookmarkOverrideOpenMode(override?.openMode)
        }))
        .filter((override) => override.folderPath && override.openMode !== 'inherit')
        .map((override) => [override.folderPath, override.openMode])
    ),
    [user?.dashboardPreferences]
  );

  const baseBookmarkGroups = React.useMemo(() => {
    const groups = new Map();
    const bookmarks = activeBookmarkData?.bookmarks || [];

    for (const bookmark of bookmarks) {
      const folderPath = bookmark.folderPath || 'Unsorted';
      if (!groups.has(folderPath)) {
        groups.set(folderPath, []);
      }
      groups.get(folderPath).push(bookmark);
    }

    return [...groups.entries()]
      .map(([folderPath, items]) => ({
        folderPath,
        items
      }))
      .sort((a, b) => a.folderPath.localeCompare(b.folderPath));
  }, [activeBookmarkData]);

  const bookmarkIdsByFolder = React.useMemo(() => {
    const mapping = {};
    for (const group of baseBookmarkGroups) {
      mapping[group.folderPath] = group.items.map((item) => item.id);
    }
    return mapping;
  }, [baseBookmarkGroups]);

  React.useEffect(() => {
    if (baseBookmarkGroups.length === 0) {
      return;
    }

    setBookmarkFolderOrder((prev) => {
      const currentFolderIds = baseBookmarkGroups.map((group) => group.folderPath);
      const merged = mergeStoredOrder(prev, currentFolderIds);
      return arraysEqual(prev, merged) ? prev : merged;
    });

    setBookmarkItemOrder((prev) => {
      const next = {};
      let changed = false;

      for (const group of baseBookmarkGroups) {
        const currentIds = group.items.map((item) => item.id);
        const merged = mergeStoredOrder(prev[group.folderPath] || [], currentIds);
        next[group.folderPath] = merged;
        if (!arraysEqual(prev[group.folderPath] || [], merged)) {
          changed = true;
        }
      }

      if (Object.keys(prev).some((folderPath) => !next[folderPath])) {
        changed = true;
      }

      return changed ? next : prev;
    });

    setCollapsedBookmarkFolders((prev) => {
      const next = { ...prev };
      let changed = false;

      for (const group of baseBookmarkGroups) {
        if (typeof next[group.folderPath] !== 'boolean') {
          next[group.folderPath] = false;
          changed = true;
        }
      }

      return changed ? next : prev;
    });

    setHiddenBookmarkFolders((prev) => {
      const next = { ...prev };
      let changed = false;

      for (const group of baseBookmarkGroups) {
        if (typeof next[group.folderPath] !== 'boolean') {
          next[group.folderPath] = false;
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [baseBookmarkGroups]);

  const orderedBookmarkGroups = React.useMemo(() => {
    const folderOrderMap = new Map(bookmarkFolderOrder.map((folderPath, index) => [folderPath, index]));

    return [...baseBookmarkGroups]
      .sort((left, right) => {
        const leftIndex = folderOrderMap.has(left.folderPath) ? folderOrderMap.get(left.folderPath) : Number.MAX_SAFE_INTEGER;
        const rightIndex = folderOrderMap.has(right.folderPath) ? folderOrderMap.get(right.folderPath) : Number.MAX_SAFE_INTEGER;
        if (leftIndex !== rightIndex) {
          return leftIndex - rightIndex;
        }
        return left.folderPath.localeCompare(right.folderPath);
      })
      .map((group) => {
        const itemOrderMap = new Map((bookmarkItemOrder[group.folderPath] || []).map((bookmarkId, index) => [bookmarkId, index]));
        const items = [...group.items].sort((left, right) => {
          const leftIndex = itemOrderMap.has(left.id) ? itemOrderMap.get(left.id) : Number.MAX_SAFE_INTEGER;
          const rightIndex = itemOrderMap.has(right.id) ? itemOrderMap.get(right.id) : Number.MAX_SAFE_INTEGER;
          if (leftIndex !== rightIndex) {
            return leftIndex - rightIndex;
          }
          if ((left.position || 0) !== (right.position || 0)) {
            return (left.position || 0) - (right.position || 0);
          }
          return left.title.localeCompare(right.title);
        });

        return {
          ...group,
          items
        };
      });
  }, [baseBookmarkGroups, bookmarkFolderOrder, bookmarkItemOrder]);

  const bookmarkGroups = React.useMemo(
    () => orderedBookmarkGroups.filter((group) => !hiddenBookmarkFolders[group.folderPath]),
    [orderedBookmarkGroups, hiddenBookmarkFolders]
  );

  const hiddenBookmarkGroups = React.useMemo(
    () => orderedBookmarkGroups.filter((group) => hiddenBookmarkFolders[group.folderPath]),
    [orderedBookmarkGroups, hiddenBookmarkFolders]
  );

  const handleFolderDrop = (targetFolderPath) => {
    if (bookmarkDrag?.type !== 'folder' || bookmarkDrag.folderPath === targetFolderPath) {
      return;
    }

    setBookmarkFolderOrder((prev) => reorderArray(prev, bookmarkDrag.folderPath, targetFolderPath));
    setBookmarkDrag(null);
  };

  const handleBookmarkDrop = (folderPath, targetBookmarkId) => {
    if (bookmarkDrag?.type !== 'bookmark' || bookmarkDrag.folderPath !== folderPath || bookmarkDrag.bookmarkId === targetBookmarkId) {
      return;
    }

    const currentIds = bookmarkIdsByFolder[folderPath] || [];
    setBookmarkItemOrder((prev) => ({
      ...prev,
      [folderPath]: reorderArray(mergeStoredOrder(prev[folderPath] || [], currentIds), bookmarkDrag.bookmarkId, targetBookmarkId)
    }));
    setBookmarkDrag(null);
  };

  const handleBookmarkGroupDropToEnd = (folderPath) => {
    if (bookmarkDrag?.type !== 'bookmark' || bookmarkDrag.folderPath !== folderPath) {
      return;
    }

    const currentIds = mergeStoredOrder(bookmarkItemOrder[folderPath] || [], bookmarkIdsByFolder[folderPath] || []);
    const withoutDragged = currentIds.filter((bookmarkId) => bookmarkId !== bookmarkDrag.bookmarkId);
    withoutDragged.push(bookmarkDrag.bookmarkId);
    setBookmarkItemOrder((prev) => ({
      ...prev,
      [folderPath]: withoutDragged
    }));
    setBookmarkDrag(null);
  };

  const visibleDashboardWidgetIds = React.useMemo(
    () => dashboardWidgetOrder.filter((widgetId) => !hiddenDashboardWidgets.includes(widgetId)),
    [dashboardWidgetOrder, hiddenDashboardWidgets]
  );

  const hiddenDashboardWidgetIds = React.useMemo(
    () => dashboardWidgetOrder.filter((widgetId) => hiddenDashboardWidgets.includes(widgetId)),
    [dashboardWidgetOrder, hiddenDashboardWidgets]
  );

  const isBookmarksWidgetVisible = visibleDashboardWidgetIds.includes('bookmarks');

  const bookmarkPackeryItemWidth = React.useMemo(() => {
    const columns = Math.max(1, bookmarkPackeryColumns);
    const totalGap = Math.max(0, columns - 1) * BOOKMARK_PACKERY_GAP;
    return `calc((100% - ${totalGap}px) / ${columns})`;
  }, [bookmarkPackeryColumns]);

  React.useEffect(() => {
    if (!isBookmarksWidgetVisible) {
      return undefined;
    }

    const element = bookmarkPackeryRef.current;
    if (!element) {
      return undefined;
    }

    const updateColumns = () => {
      const width = element.clientWidth;
      if (!width) {
        return;
      }

      const nextColumns = Math.max(1, Math.floor((width + BOOKMARK_PACKERY_GAP) / (BOOKMARK_TILE_MIN_WIDTH + BOOKMARK_PACKERY_GAP)));
      setBookmarkPackeryColumns((prev) => (prev === nextColumns ? prev : nextColumns));
    };

    updateColumns();

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      updateColumns();
      bookmarkPackeryInstanceRef.current?.shiftLayout();
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [isBookmarksWidgetVisible, bookmarkGroups.length, isTasksDrawerPinned]);

  React.useEffect(() => {
    const container = bookmarkPackeryRef.current;

    const destroyPackery = () => {
      const instance = bookmarkPackeryInstanceRef.current;

      for (const draggie of bookmarkPackeryDraggiesRef.current) {
        instance?.unbindDraggabillyEvents(draggie);
        draggie.destroy();
      }

      bookmarkPackeryDraggiesRef.current = [];

      if (instance) {
        instance.destroy();
        bookmarkPackeryInstanceRef.current = null;
      }
    };

    if (!isBookmarksWidgetVisible || !container || bookmarkGroups.length === 0) {
      destroyPackery();
      return destroyPackery;
    }

    destroyPackery();

    const instance = new Packery(container, {
      itemSelector: '.bookmark-packery-item',
      columnWidth: '.bookmark-packery-sizer',
      gutter: BOOKMARK_PACKERY_GAP,
      percentPosition: true,
      transitionDuration: '0.18s'
    });

    bookmarkPackeryInstanceRef.current = instance;

    const persistVisibleFolderOrder = () => {
      const visibleOrder = instance
        .getItemElements()
        .map((item) => item.getAttribute('data-folder-path'))
        .filter(Boolean);

      if (visibleOrder.length === 0) {
        return;
      }

      const allFolderPaths = orderedBookmarkGroups.map((group) => group.folderPath);
      setBookmarkFolderOrder((prev) => {
        const next = mergeVisibleOrderIntoFullOrder(prev, visibleOrder, allFolderPaths);
        return arraysEqual(prev, next) ? prev : next;
      });
    };

    container.querySelectorAll('.bookmark-packery-item').forEach((item) => {
      const draggie = new Draggabilly(item, {
        handle: '.bookmark-packery-handle'
      });
      instance.bindDraggabillyEvents(draggie);
      bookmarkPackeryDraggiesRef.current.push(draggie);
    });

    instance.on('dragItemPositioned', persistVisibleFolderOrder);
    instance.reloadItems();
    instance.layout();

    return () => {
      instance.off('dragItemPositioned', persistVisibleFolderOrder);
      destroyPackery();
    };
  }, [bookmarkGroups, orderedBookmarkGroups, bookmarkPackeryItemWidth, isBookmarksWidgetVisible]);

  const renderWidgetControls = (widgetId) => {
    const index = dashboardWidgetOrder.indexOf(widgetId);
    const isFirst = index <= 0;
    const isLast = index === dashboardWidgetOrder.length - 1;

    return (
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => handleMoveWidget(widgetId, 'up')}
          disabled={isFirst}
          className="rounded-md border border-gray-200 bg-white p-1.5 text-gray-400 transition hover:border-gray-300 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-40"
          title="Move up"
        >
          <FiArrowUp className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => handleMoveWidget(widgetId, 'down')}
          disabled={isLast}
          className="rounded-md border border-gray-200 bg-white p-1.5 text-gray-400 transition hover:border-gray-300 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-40"
          title="Move down"
        >
          <FiArrowDown className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => handleHideWidget(widgetId)}
          className="rounded-md border border-gray-200 bg-white p-1.5 text-gray-400 transition hover:border-gray-300 hover:text-gray-600"
          title="Hide widget"
        >
          <FiEyeOff className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  };

  return (
    <div className={`min-h-screen bg-gray-50 transition-[padding] duration-200 ${isTasksDrawerPinned ? 'lg:pl-[42rem]' : ''}`}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img src="/futurenote-icon.svg" alt="FutureNote" className="h-9 w-9" />
              <h1 className="ml-2 text-xl font-semibold text-gray-900">FutureNote</h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <FiUser className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-700">
                  {user?.firstName} {user?.lastName}
                </span>
              </div>

              <button
                type="button"
                onClick={handleOpenTasksDrawer}
                className="inline-flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-600 transition-colors hover:border-gray-300 hover:text-gray-900"
              >
                <FiCheckSquare className="h-4 w-4" />
                Tasks
              </button>
              
              <Link
                to="/settings"
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiSettings className="h-5 w-5" />
              </Link>
              
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiLogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {visibleDashboardWidgetIds.length === 0 && hiddenDashboardWidgetIds.length > 0 && (
          <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
            All home page widgets are currently hidden. Open{' '}
            <Link to="/settings?tab=home" className="font-medium text-primary-600 hover:text-primary-700">
              Settings / Home Page
            </Link>{' '}
            to show them again.
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {visibleDashboardWidgetIds.map((widgetId) => {
            if (widgetId === 'welcome') {
              return (
                <div key={widgetId} className="card lg:col-span-2">
                  <div className="mb-4 flex items-start justify-between gap-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Overview</p>
                    {renderWidgetControls(widgetId)}
                  </div>
                  <h2 className="mb-2 text-2xl font-bold text-gray-900">
                    Welcome back, {user?.firstName}!
                  </h2>
                  <p className="text-gray-600">
                    Here's what's coming up in your calendar today.
                  </p>
                </div>
              );
            }

            if (widgetId === 'upcoming_events') {
              return (
                <div key={widgetId} className="card lg:col-span-2">
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <h3 className="text-lg font-semibold text-gray-900">Upcoming Events</h3>
                    <div className="flex items-center gap-3">
                      <Link
                        to="/calendar"
                        className="text-sm font-medium text-primary-600 hover:text-primary-700"
                      >
                        View all
                      </Link>
                      {renderWidgetControls(widgetId)}
                    </div>
                  </div>

                  {eventsLoading ? (
                    <div className="flex justify-center py-8">
                      <LoadingSpinner />
                    </div>
                  ) : events && events.length > 0 ? (
                    <div className="space-y-3">
                      {events.map((event) => {
                        const latestNotification = event.notificationsSent?.filter((n) => n.status === 'success')
                          .sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt))[0];
                        const notificationTypeLabels = {
                          call: 'Call',
                          sms: 'SMS',
                          sms_pre: 'SMS (pre-call)'
                        };
                        const notificationsEnabled = event.notificationSettings?.enabled !== false;
                        const notificationTime = event.notificationSettings?.notificationTime ?? 15;
                        const isHovered = hoveredEventId === event.id;
                        const isToggling = toggleNotificationsMutation.isLoading && toggleNotificationsMutation.variables?.id === event.id;
                        const isUpdatingTime = updateNotificationTimeMutation.isLoading && updateNotificationTimeMutation.variables?.id === event.id;

                        return (
                          <div
                            key={event.id}
                            onMouseEnter={() => setHoveredEventId(event.id)}
                            onMouseLeave={() => setHoveredEventId(null)}
                            className={`flex items-center rounded-lg border p-3 transition-all duration-150 ${
                              notificationsEnabled
                                ? 'border-gray-200 bg-gray-50 hover:border-primary-200'
                                : 'border-red-200 bg-red-50'
                            }`}
                          >
                            <div className="flex-shrink-0">
                              <div className={`h-3 w-3 rounded-full ${notificationsEnabled ? 'bg-primary-600' : 'bg-red-500'}`} />
                            </div>
                            <div className="ml-3 flex-1">
                              <div className="flex items-center justify-between space-x-2">
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900">{event.title}</h4>
                                  <div className="mt-1 flex items-center text-xs text-gray-500">
                                    <FiClock className="mr-1 h-3 w-3" />
                                    {new Date(event.startTime).toLocaleString()}
                                    {event.location && (
                                      <>
                                        <span className="mx-2">ג€¢</span>
                                        {event.location}
                                      </>
                                    )}
                                  </div>
                                  {latestNotification && notificationsEnabled && (
                                    <div className="mt-1 text-xs text-green-600">
                                      Last notification sent via {notificationTypeLabels[latestNotification.type] || latestNotification.type.toUpperCase()} on{' '}
                                      {new Date(latestNotification.sentAt).toLocaleString()}
                                    </div>
                                  )}
                                  {!notificationsEnabled && (
                                    <div className="mt-1 text-xs font-medium text-red-600">
                                      Notifications disabled for this event
                                    </div>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleToggleNotifications(event)}
                                  disabled={isToggling}
                                  className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium transition-opacity ${
                                    notificationsEnabled
                                      ? 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                                      : 'bg-red-100 text-red-700 hover:bg-red-200'
                                  } ${isHovered ? 'opacity-100' : 'opacity-70'}`}
                                  title={notificationsEnabled ? 'Disable notifications for this event' : 'Enable notifications for this event'}
                                >
                                  {notificationsEnabled ? <FiBell className="h-4 w-4" /> : <FiBellOff className="h-4 w-4" />}
                                  {notificationsEnabled ? 'On' : 'Off'}
                                </button>
                              </div>
                              <div className="mt-3 flex items-center gap-2">
                                <span className="text-xs text-gray-500">Notify</span>
                                <select
                                  value={notificationTime}
                                  onChange={(e) => {
                                    const nextValue = parseInt(e.target.value, 10);
                                    updateNotificationTimeMutation.mutate({ id: event.id, notificationTime: nextValue });
                                  }}
                                  disabled={!notificationsEnabled || isUpdatingTime}
                                  className="input-field w-28 px-2 py-1 text-xs"
                                >
                                  {[30, 15, 10, 5, 2].map((minutes) => (
                                    <option key={minutes} value={minutes}>
                                      {minutes} min
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="ml-3 flex-shrink-0">
                              <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium capitalize text-blue-800">
                                {event.calendarProvider}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="py-8 text-center">
                      <FiCalendar className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No upcoming events</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Connect your calendar to see your events here.
                      </p>
                    </div>
                  )}
                </div>
              );
            }

            if (widgetId === 'quick_actions') {
              return (
                <div key={widgetId} className="card lg:col-span-1">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
                    {renderWidgetControls(widgetId)}
                  </div>
                  <div className="space-y-3">
                    <Link
                      to="/calendar"
                      className="btn-primary flex w-full items-center justify-center"
                    >
                      <FiCalendar className="mr-2 h-4 w-4" />
                      View Calendar
                    </Link>
                    <Link
                      to="/settings"
                      className="btn-secondary flex w-full items-center justify-center"
                    >
                      <FiSettings className="mr-2 h-4 w-4" />
                      Settings
                    </Link>
                  </div>
                </div>
              );
            }

            if (widgetId === 'calendar_connections') {
              return (
                <div key={widgetId} className="card lg:col-span-1">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <h3 className="text-lg font-semibold text-gray-900">Calendar Connections</h3>
                    {renderWidgetControls(widgetId)}
                  </div>
                  {statusLoading ? (
                    <div className="flex justify-center py-4">
                      <LoadingSpinner size="sm" />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
                        <div className="flex items-center">
                          <div className="mr-3 h-3 w-3 rounded-full bg-red-500" />
                          <span className="text-sm font-medium text-gray-900">Google Calendar</span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {calendarStatus?.google?.connected ? 'Connected' : 'Not connected'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
                        <div className="flex items-center">
                          <div className="mr-3 h-3 w-3 rounded-full bg-blue-500" />
                          <span className="text-sm font-medium text-gray-900">Microsoft Calendar</span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {calendarStatus?.microsoft?.connected ? 'Connected' : 'Not connected'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            if (widgetId === 'notification_status') {
              return (
                <div key={widgetId} className="card lg:col-span-1">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                    {renderWidgetControls(widgetId)}
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center rounded-lg border border-green-200 bg-green-50 p-3">
                      <FiBell className="mr-3 h-5 w-5 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-green-800">Reminders Enabled</p>
                        <p className="text-xs text-green-600">
                          Default: {(user?.notificationPreferences?.defaultType || 'call').toUpperCase()} - {user?.notificationPreferences?.defaultTime ?? 15} min before event
                        </p>
                      </div>
                    </div>
                    {user?.notificationPreferences?.preCallSms?.enabled && (
                      <div className="flex items-center rounded-lg border border-blue-200 bg-blue-50 p-3">
                        <FiBell className="mr-3 h-5 w-5 text-blue-600" />
                        <div>
                          <p className="text-sm font-medium text-blue-800">Pre-call SMS Reminder</p>
                          <p className="text-xs text-blue-600">
                            SMS {user.notificationPreferences.preCallSms.minutesBeforeCall ?? 15} min before the call
                          </p>
                        </div>
                      </div>
                    )}
                    {user?.notificationPreferences?.quietHours?.enabled && (
                      <div className="flex items-center rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <FiBellOff className="mr-3 h-5 w-5 text-slate-600" />
                        <div>
                          <p className="text-sm font-medium text-slate-800">Quiet Hours</p>
                          <p className="text-xs text-slate-600">
                            {user.notificationPreferences.quietHours.start || '22:00'} - {user.notificationPreferences.quietHours.end || '07:00'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            }

            if (widgetId === 'chats') {
              return (
                <div key={widgetId} className="card lg:col-span-3">
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Chats</h3>
                      <p className="text-sm text-gray-500">Generate guest chat links and track visitors.</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setIsCreateChatOpen(true)}
                        className="btn-primary flex items-center"
                      >
                        <FiPlus className="mr-2 h-4 w-4" />
                        New Chat
                      </button>
                      {renderWidgetControls(widgetId)}
                    </div>
                  </div>

                  {chatsLoading ? (
                    <div className="flex justify-center py-6">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div className="flex items-stretch space-x-4 overflow-x-auto pb-2">
                      <button
                        type="button"
                        onClick={() => setIsCreateChatOpen(true)}
                        className="flex h-40 min-w-[220px] flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-gray-500 transition hover:border-gray-400 hover:text-gray-700"
                      >
                        <FiPlus className="mb-2 h-6 w-6" />
                        <span className="text-sm font-medium">Create new chat</span>
                      </button>

                      {chats.map((chat) => {
                        const chatLink = `${baseUrl}/chat/${chat.publicId}`;
                        const lastMessageLabel = chat.lastMessageAt
                          ? new Date(chat.lastMessageAt).toLocaleString()
                          : `Created ${new Date(chat.createdAt).toLocaleDateString()}`;

                        return (
                          <Link
                            key={chat.id}
                            to={`/chat/${chat.publicId}/admin`}
                            className="relative h-40 min-w-[260px] overflow-hidden rounded-xl border border-gray-200 bg-slate-900 text-white shadow-sm"
                            style={
                              chat.backgroundImage
                                ? {
                                    backgroundImage: `url(${chat.backgroundImage})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center'
                                  }
                                : undefined
                            }
                          >
                            <div className={`absolute inset-0 ${chat.backgroundImage ? 'bg-black/40' : 'bg-gradient-to-br from-slate-800 to-slate-900'}`} />
                            <div className="relative z-10 flex h-full flex-col justify-between p-4">
                              <div className="flex items-start justify-between">
                                <div className="flex items-center gap-2">
                                  <FiMessageSquare className="h-4 w-4" />
                                  <span className="text-sm font-semibold">{chat.title}</span>
                                </div>
                                {chat.newGuestCount > 0 && (
                                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
                                    {chat.newGuestCount}
                                  </span>
                                )}
                              </div>

                              <div>
                                <p className="text-xs text-white/80">
                                  {chat.lastMessagePreview ? `Last: ${chat.lastMessagePreview}` : 'No messages yet'}
                                </p>
                                <div className="mt-3 flex items-center justify-between text-xs text-white/70">
                                  <span>{lastMessageLabel}</span>
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      setQrChat({ ...chat, link: chatLink });
                                    }}
                                    className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-1 text-white hover:bg-white/25"
                                  >
                                    <FiLink className="h-3 w-3" />
                                    QR
                                  </button>
                                </div>
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            if (widgetId === 'bookmarks') {
              return (
                <div key={widgetId} className="card lg:col-span-3">
                  <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Bookmarks</h3>
                      <p className="text-sm text-gray-500">Synced from the FutureNote Edge extension. Drag folders and icons to reorder.</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="text-right text-xs text-gray-500">
                        <div>{activeBookmarkData?.totalCount || 0} bookmark{activeBookmarkData?.totalCount === 1 ? '' : 's'} loaded</div>
                        {activeBookmarkData?.lastSyncedAt && (
                          <div>Last sync {new Date(activeBookmarkData.lastSyncedAt).toLocaleString()}</div>
                        )}
                      </div>
                      {renderWidgetControls(widgetId)}
                    </div>
                  </div>

                  {bookmarksLoading && !activeBookmarkData ? (
                    <div className="flex justify-center py-6">
                      <LoadingSpinner />
                    </div>
                  ) : bookmarkGroups.length === 0 && hiddenBookmarkGroups.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-500">
                      No bookmarks synced yet. Load the Edge extension, sign in with your FutureNote account, and run a sync.
                    </div>
                  ) : bookmarkGroups.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-600">
                      All bookmark folders are currently hidden. Open{' '}
                      <Link to="/settings?tab=home" className="font-medium text-primary-600 hover:text-primary-700">
                        Settings / Home Page
                      </Link>{' '}
                      to show them again.
                    </div>
                  ) : (
                    <div ref={bookmarkPackeryRef} className="relative min-h-[1px]">
                      <div
                        aria-hidden="true"
                        className="bookmark-packery-sizer"
                        style={{ width: bookmarkPackeryItemWidth, height: 0 }}
                      />
                      {bookmarkGroups.map((group) => {
                        const isCollapsed = !!collapsedBookmarkFolders[group.folderPath];
                        const tileMeta = getBookmarkTileMeta(group.items.length, isCollapsed);
                        const visibleBookmarks = isCollapsed
                          ? group.items.slice(0, tileMeta.previewCount)
                          : group.items;
                        const hiddenCount = Math.max(0, group.items.length - visibleBookmarks.length);
                        const folderSegments = getBookmarkFolderSegments(group.folderPath);
                        const folderLabel = getBookmarkFolderLabel(group.folderPath);

                        return (
                          <div
                            key={group.folderPath}
                            data-folder-path={group.folderPath}
                            className="bookmark-packery-item"
                            style={{ width: bookmarkPackeryItemWidth }}
                          >
                            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition">
                              <div className="flex h-full">
                                <div className="bookmark-packery-handle flex shrink-0 touch-none cursor-grab overflow-hidden border-r border-slate-200 bg-slate-50 active:cursor-grabbing">
                                  {folderSegments.map((segment, index) => (
                                    <div
                                      key={`${group.folderPath}-${segment}-${index}`}
                                      className={`flex w-7 items-center justify-center border-r border-white/70 px-1 ${
                                        index === folderSegments.length - 1
                                          ? 'bg-white/70'
                                          : index % 2 === 0
                                            ? 'bg-slate-100'
                                            : 'bg-slate-50'
                                      }`}
                                    >
                                      <span
                                        className={`${isCollapsed ? 'text-[9px]' : 'text-[10px]'} font-semibold uppercase tracking-[0.18em] text-slate-500`}
                                        style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                                        title={segment}
                                      >
                                        {segment}
                                      </span>
                                    </div>
                                  ))}
                                </div>

                                <div className={`flex min-w-0 flex-1 flex-col ${isCollapsed ? 'p-3' : 'p-4'}`}>
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="bookmark-packery-handle min-w-0 touch-none cursor-grab active:cursor-grabbing">
                                      <div className="flex items-center gap-2 text-amber-500">
                                        <FiFolder className={`${isCollapsed ? 'h-3.5 w-3.5' : 'h-4 w-4'} flex-shrink-0`} />
                                        <span className={`rounded-full bg-gray-100 text-gray-500 ${isCollapsed ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-[11px] uppercase tracking-wide'}`}>
                                          {group.items.length}
                                        </span>
                                      </div>
                                      {!isCollapsed && (
                                        <p className="mt-2 truncate text-xs font-medium text-slate-400" title={folderLabel}>
                                          {folderLabel}
                                        </p>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => setCollapsedBookmarkFolders((prev) => ({
                                          ...prev,
                                          [group.folderPath]: !prev[group.folderPath]
                                        }))}
                                        className="rounded-full border border-gray-200 bg-white p-1.5 text-gray-400 transition hover:border-gray-300 hover:text-gray-600"
                                        title={isCollapsed ? 'Expand folder' : 'Collapse folder'}
                                      >
                                        {isCollapsed ? (
                                          <FiChevronRight className="h-4 w-4 flex-shrink-0" />
                                        ) : (
                                          <FiChevronDown className="h-4 w-4 flex-shrink-0" />
                                        )}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          setHiddenBookmarkFolders((prev) => ({
                                            ...prev,
                                            [group.folderPath]: true
                                          }));
                                        }}
                                        className="rounded-full border border-gray-200 bg-white p-1.5 text-gray-400 transition hover:border-gray-300 hover:text-gray-600"
                                        title="Hide folder"
                                      >
                                        <FiEyeOff className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                  </div>

                                <div
                                  className="mt-3 flex-1"
                                  onDragOver={(event) => {
                                    if (bookmarkDrag?.type === 'bookmark' && bookmarkDrag.folderPath === group.folderPath) {
                                      event.preventDefault();
                                    }
                                  }}
                                  onDrop={(event) => {
                                    if (bookmarkDrag?.type === 'bookmark' && bookmarkDrag.folderPath === group.folderPath) {
                                      event.preventDefault();
                                      handleBookmarkGroupDropToEnd(group.folderPath);
                                    }
                                  }}
                                >
                                  <div
                                    className="grid gap-2"
                                    style={{
                                      gridTemplateColumns: `repeat(${tileMeta.columns}, minmax(0, 1fr))`
                                    }}
                                  >
                                    {visibleBookmarks.map((bookmark) => {
                                      const iconUrl = getBookmarkDisplayIcon(bookmark);
                                      const hasIconError = bookmarkIconErrors[bookmark.id];

                                      return (
                                        <a
                                          key={bookmark.id}
                                          href={bookmark.url}
                                          target={getEffectiveBookmarkOpenMode(bookmark, bookmarkFolderOverrideMap, bookmarkDefaultOpenMode) === 'new_tab' ? '_blank' : undefined}
                                          rel={getEffectiveBookmarkOpenMode(bookmark, bookmarkFolderOverrideMap, bookmarkDefaultOpenMode) === 'new_tab' ? 'noreferrer' : undefined}
                                          draggable
                                          onDragStart={() => setBookmarkDrag({
                                            type: 'bookmark',
                                            folderPath: group.folderPath,
                                            bookmarkId: bookmark.id
                                          })}
                                          onDragEnd={() => setBookmarkDrag(null)}
                                          onDragOver={(event) => {
                                            if (bookmarkDrag?.type === 'bookmark' && bookmarkDrag.folderPath === group.folderPath) {
                                              event.preventDefault();
                                              event.stopPropagation();
                                            }
                                          }}
                                          onDrop={(event) => {
                                            if (bookmarkDrag?.type === 'bookmark' && bookmarkDrag.folderPath === group.folderPath) {
                                              event.preventDefault();
                                              event.stopPropagation();
                                              handleBookmarkDrop(group.folderPath, bookmark.id);
                                            }
                                          }}
                                          className={`group flex flex-col items-center rounded-xl border px-2 py-2 text-center transition ${
                                            isCollapsed ? 'min-h-[48px] justify-center' : 'min-h-[76px] justify-start'
                                          } ${
                                            bookmarkDrag?.type === 'bookmark' && bookmarkDrag.bookmarkId === bookmark.id
                                              ? 'border-primary-300 bg-primary-50'
                                              : 'border-gray-100 bg-gray-50 hover:border-primary-200 hover:bg-primary-50'
                                          }`}
                                          title={bookmark.title}
                                        >
                                          <span className={`flex items-center justify-center overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-200 ${
                                            isCollapsed ? 'h-7 w-7' : 'h-9 w-9'
                                          }`}>
                                            {!hasIconError && iconUrl ? (
                                              <img
                                                src={iconUrl}
                                                alt=""
                                                className={isCollapsed ? 'h-[18px] w-[18px] object-contain' : 'h-6 w-6 object-contain'}
                                                onError={() => setBookmarkIconErrors((prev) => ({ ...prev, [bookmark.id]: true }))}
                                              />
                                            ) : (
                                              <span className="text-[10px] font-semibold text-gray-500">
                                                {getBookmarkInitials(bookmark.title, bookmark.url)}
                                              </span>
                                            )}
                                          </span>
                                          {!isCollapsed && (
                                            <span className="mt-2 line-clamp-2 text-[11px] font-medium leading-4 text-gray-700">
                                              {bookmark.title}
                                            </span>
                                          )}
                                        </a>
                                      );
                                    })}
                                  </div>

                                  {hiddenCount > 0 && (
                                    <div className={`text-center font-medium text-gray-400 ${isCollapsed ? 'mt-2 text-[10px]' : 'mt-3 text-[11px]'}`}>
                                      + {hiddenCount} more bookmark{hiddenCount === 1 ? '' : 's'}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            if (widgetId === 'future_notes') {
              return (
                <div key={widgetId} className="card lg:col-span-3">
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">FutureNotes</h3>
                      <p className="text-sm text-gray-500">Publish updates for all logged-in users.</p>
                    </div>
                    {renderWidgetControls(widgetId)}
                  </div>

                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <div className="rounded-lg border border-gray-200 p-4">
                      <div className="mb-3 flex items-center gap-2 text-sm font-medium text-gray-900">
                        <FiEdit className="h-4 w-4 text-gray-400" />
                        Publish new note
                      </div>
                      <textarea
                        value={noteContent}
                        onChange={(event) => setNoteContent(event.target.value)}
                        className="input-field h-32"
                        placeholder="Write your note in Markdown..."
                        maxLength={5000}
                      />
                      <div className="mt-3 flex flex-wrap items-center gap-3">
                        <input
                          type="datetime-local"
                          value={notePublishAt}
                          onChange={(event) => setNotePublishAt(event.target.value)}
                          className="input-field"
                        />
                        <button
                          type="button"
                          onClick={handleCreateNote}
                          className="btn-primary"
                          disabled={createNoteMutation.isLoading}
                        >
                          {createNoteMutation.isLoading ? 'Publishing...' : 'Publish'}
                        </button>
                      </div>
                      <p className="mt-2 text-xs text-gray-500">Notes appear for everyone after the publish time.</p>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <p className="mb-2 text-xs font-semibold text-gray-500">Preview</p>
                      {noteContent.trim() ? (
                        <div
                          className="space-y-2 text-sm text-gray-700"
                          dangerouslySetInnerHTML={{ __html: renderMarkdown(noteContent) }}
                        />
                      ) : (
                        <p className="text-sm text-gray-400">Start typing to see a preview.</p>
                      )}
                    </div>
                  </div>

                  <div className="mt-6">
                    <h4 className="mb-3 text-sm font-semibold text-gray-900">Latest notes</h4>
                    {notesLoading ? (
                      <div className="flex justify-center py-6">
                        <LoadingSpinner />
                      </div>
                    ) : notes.length === 0 ? (
                      <p className="text-sm text-gray-500">No published notes yet.</p>
                    ) : (
                      <div className="columns-1 gap-4 sm:columns-2 lg:columns-3">
                        {notes.map((note) => (
                          <div key={note.id} className="note-tile mb-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                            <div
                              className="space-y-2 text-sm text-gray-700"
                              dangerouslySetInnerHTML={{ __html: renderMarkdown(note.content) }}
                            />
                            <div className="mt-3 text-xs text-gray-400">
                              {new Date(note.publishAt).toLocaleString()}
                              {note.author?.firstName && (
                                <> ֲ· {note.author.firstName} {note.author.lastName}</>
                              )}
                            </div>

                            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                              <button
                                type="button"
                                className={`rounded-full border px-2 py-1 ${note.userReaction === 'like' ? 'border-primary-500 text-primary-600' : 'border-gray-200'}`}
                                onClick={() => reactionMutation.mutate({
                                  noteId: note.id,
                                  type: note.userReaction === 'like' ? 'none' : 'like'
                                })}
                              >
                                Like ֲ· {note.likesCount || 0}
                              </button>
                              <button
                                type="button"
                                className={`rounded-full border px-2 py-1 ${note.userReaction === 'dislike' ? 'border-red-500 text-red-600' : 'border-gray-200'}`}
                                onClick={() => reactionMutation.mutate({
                                  noteId: note.id,
                                  type: note.userReaction === 'dislike' ? 'none' : 'dislike'
                                })}
                              >
                                Dislike ֲ· {note.dislikesCount || 0}
                              </button>
                              <button
                                type="button"
                                className="rounded-full border border-gray-200 px-2 py-1"
                                onClick={() => setExpandedNoteId((prev) => (prev === note.id ? null : note.id))}
                              >
                                Comments ֲ· {note.commentsCount || 0}
                              </button>
                            </div>

                            {expandedNoteId === note.id && (
                              <div className="mt-4 space-y-3">
                                {commentsLoading ? (
                                  <div className="text-xs text-gray-400">Loading comments...</div>
                                ) : noteComments.length === 0 ? (
                                  <div className="text-xs text-gray-400">No comments yet.</div>
                                ) : (
                                  <div className="space-y-2">
                                    {noteComments.map((comment) => (
                                      <div key={comment.id} className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-700">
                                        <div className="font-semibold">
                                          {comment.author?.firstName} {comment.author?.lastName}
                                        </div>
                                        <div className="mt-1">{comment.content}</div>
                                        <div className="mt-1 text-[10px] text-gray-400">
                                          {new Date(comment.createdAt).toLocaleString()}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                <div className="flex items-center gap-2">
                                  <input
                                    value={commentDrafts[note.id] || ''}
                                    onChange={(event) =>
                                      setCommentDrafts((prev) => ({ ...prev, [note.id]: event.target.value }))
                                    }
                                    className="input-field text-sm"
                                    placeholder="Add a comment"
                                  />
                                  <button
                                    type="button"
                                    className="btn-secondary text-sm"
                                    onClick={() => {
                                      const content = (commentDrafts[note.id] || '').trim();
                                      if (!content) {
                                        toast.error('Write a comment first');
                                        return;
                                      }
                                      commentMutation.mutate({ noteId: note.id, content });
                                    }}
                                    disabled={commentMutation.isLoading}
                                  >
                                    Post
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            }

            return null;
          })}
        </div>

        {false && (
        <>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Welcome Section */}
            <div className="card">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Welcome back, {user?.firstName}!
              </h2>
              <p className="text-gray-600">
                Here's what's coming up in your calendar today.
              </p>
            </div>

            {/* Upcoming Events */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Upcoming Events</h3>
                <Link
                  to="/calendar"
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  View all
                </Link>
              </div>

              {eventsLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : events && events.length > 0 ? (
                <div className="space-y-3">
                  {events.map((event) => {
                    const latestNotification = event.notificationsSent?.filter(n => n.status === 'success')
                      .sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt))[0];
                    const notificationTypeLabels = {
                      call: 'Call',
                      sms: 'SMS',
                      sms_pre: 'SMS (pre-call)'
                    };
                    const notificationsEnabled = event.notificationSettings?.enabled !== false;
                    const notificationTime = event.notificationSettings?.notificationTime ?? 15;
                    const isHovered = hoveredEventId === event.id;
                    const isToggling = toggleNotificationsMutation.isLoading && toggleNotificationsMutation.variables?.id === event.id;
                    const isUpdatingTime = updateNotificationTimeMutation.isLoading && updateNotificationTimeMutation.variables?.id === event.id;

                    return (
                      <div
                        key={event.id}
                        onMouseEnter={() => setHoveredEventId(event.id)}
                        onMouseLeave={() => setHoveredEventId(null)}
                        className={`flex items-center p-3 rounded-lg border transition-all duration-150 ${
                          notificationsEnabled
                            ? 'bg-gray-50 border-gray-200 hover:border-primary-200'
                            : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <div className="flex-shrink-0">
                          <div className={`w-3 h-3 rounded-full ${notificationsEnabled ? 'bg-primary-600' : 'bg-red-500'}`}></div>
                        </div>
                        <div className="ml-3 flex-1">
                          <div className="flex items-center justify-between space-x-2">
                            <div>
                              <h4 className="text-sm font-medium text-gray-900">{event.title}</h4>
                              <div className="flex items-center text-xs text-gray-500 mt-1">
                                <FiClock className="h-3 w-3 mr-1" />
                                {new Date(event.startTime).toLocaleString()}
                                {event.location && (
                                  <>
                                    <span className="mx-2">•</span>
                                    {event.location}
                                  </>
                                )}
                              </div>
                              {latestNotification && notificationsEnabled && (
                                <div className="text-xs text-green-600 mt-1">
                                  Last notification sent via {notificationTypeLabels[latestNotification.type] || latestNotification.type.toUpperCase()} on{' '}
                                  {new Date(latestNotification.sentAt).toLocaleString()}
                                </div>
                              )}
                              {!notificationsEnabled && (
                                <div className="text-xs text-red-600 mt-1 font-medium">
                                  Notifications disabled for this event
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleToggleNotifications(event)}
                              disabled={isToggling}
                              className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium transition-opacity ${
                                notificationsEnabled
                                  ? 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                                  : 'bg-red-100 text-red-700 hover:bg-red-200'
                              } ${isHovered ? 'opacity-100' : 'opacity-70'}`}
                              title={notificationsEnabled ? 'Disable notifications for this event' : 'Enable notifications for this event'}
                            >
                              {notificationsEnabled ? <FiBell className="h-4 w-4" /> : <FiBellOff className="h-4 w-4" />}
                              {notificationsEnabled ? 'On' : 'Off'}
                            </button>
                          </div>
                          <div className="mt-3 flex items-center gap-2">
                            <span className="text-xs text-gray-500">Notify</span>
                            <select
                              value={notificationTime}
                              onChange={(e) => {
                                const nextValue = parseInt(e.target.value, 10);
                                updateNotificationTimeMutation.mutate({ id: event.id, notificationTime: nextValue });
                              }}
                              disabled={!notificationsEnabled || isUpdatingTime}
                              className="input-field text-xs py-1 px-2 w-28"
                            >
                              {[30, 15, 10, 5, 2].map((minutes) => (
                                <option key={minutes} value={minutes}>
                                  {minutes} min
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="flex-shrink-0 ml-3">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                            {event.calendarProvider}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FiCalendar className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No upcoming events</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Connect your calendar to see your events here.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <Link
                  to="/calendar"
                  className="btn-primary w-full flex items-center justify-center"
                >
                  <FiCalendar className="h-4 w-4 mr-2" />
                  View Calendar
                </Link>
                
                <Link
                  to="/settings"
                  className="btn-secondary w-full flex items-center justify-center"
                >
                  <FiSettings className="h-4 w-4 mr-2" />
                  Settings
                </Link>
              </div>
            </div>

            {/* Calendar Connections */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Calendar Connections</h3>
              
              {statusLoading ? (
                <div className="flex justify-center py-4">
                  <LoadingSpinner size="sm" />
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
                      <span className="text-sm font-medium text-gray-900">Google Calendar</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {calendarStatus?.google?.connected ? 'Connected' : 'Not connected'}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                      <span className="text-sm font-medium text-gray-900">Microsoft Calendar</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {calendarStatus?.microsoft?.connected ? 'Connected' : 'Not connected'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Notification Status */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Notifications</h3>
              <div className="space-y-3">
                <div className="flex items-center p-3 bg-green-50 rounded-lg border border-green-200">
                  <FiBell className="h-5 w-5 text-green-600 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Reminders Enabled</p>
                    <p className="text-xs text-green-600">
                      Default: {(user?.notificationPreferences?.defaultType || 'call').toUpperCase()} - {user?.notificationPreferences?.defaultTime ?? 15} min before event
                    </p>
                  </div>
                </div>
                {user?.notificationPreferences?.preCallSms?.enabled && (
                  <div className="flex items-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <FiBell className="h-5 w-5 text-blue-600 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-blue-800">Pre-call SMS Reminder</p>
                      <p className="text-xs text-blue-600">
                        SMS {user.notificationPreferences.preCallSms.minutesBeforeCall ?? 15} min before the call
                      </p>
                    </div>
                  </div>
                )}
                {user?.notificationPreferences?.quietHours?.enabled && (
                  <div className="flex items-center p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <FiBellOff className="h-5 w-5 text-slate-600 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-slate-800">Quiet Hours</p>
                      <p className="text-xs text-slate-600">
                        {user.notificationPreferences.quietHours.start || '22:00'} - {user.notificationPreferences.quietHours.end || '07:00'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <div className="card">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Chats</h3>
                <p className="text-sm text-gray-500">Generate guest chat links and track visitors.</p>
              </div>
              <button
                onClick={() => setIsCreateChatOpen(true)}
                className="btn-primary flex items-center"
              >
                <FiPlus className="h-4 w-4 mr-2" />
                New Chat
              </button>
            </div>

            {chatsLoading ? (
              <div className="flex justify-center py-6">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="flex items-stretch space-x-4 overflow-x-auto pb-2">
                <button
                  type="button"
                  onClick={() => setIsCreateChatOpen(true)}
                  className="min-w-[220px] h-40 rounded-xl border border-dashed border-gray-300 bg-gray-50 flex flex-col items-center justify-center text-gray-500 hover:text-gray-700 hover:border-gray-400 transition"
                >
                  <FiPlus className="h-6 w-6 mb-2" />
                  <span className="text-sm font-medium">Create new chat</span>
                </button>

                {chats.map((chat) => {
                  const chatLink = `${baseUrl}/chat/${chat.publicId}`;
                  const lastMessageLabel = chat.lastMessageAt
                    ? new Date(chat.lastMessageAt).toLocaleString()
                    : `Created ${new Date(chat.createdAt).toLocaleDateString()}`;

                  return (
                    <Link
                      key={chat.id}
                      to={`/chat/${chat.publicId}/admin`}
                      className="relative min-w-[260px] h-40 rounded-xl overflow-hidden border border-gray-200 shadow-sm bg-slate-900 text-white"
                      style={
                        chat.backgroundImage
                          ? {
                              backgroundImage: `url(${chat.backgroundImage})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center'
                            }
                          : undefined
                      }
                    >
                      <div className={`absolute inset-0 ${chat.backgroundImage ? 'bg-black/40' : 'bg-gradient-to-br from-slate-800 to-slate-900'}`} />
                      <div className="relative z-10 h-full flex flex-col justify-between p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <FiMessageSquare className="h-4 w-4" />
                            <span className="text-sm font-semibold">{chat.title}</span>
                          </div>
                          {chat.newGuestCount > 0 && (
                            <span className="inline-flex items-center justify-center text-xs font-bold rounded-full bg-red-500 text-white h-6 w-6">
                              {chat.newGuestCount}
                            </span>
                          )}
                        </div>

                        <div>
                          <p className="text-xs text-white/80">
                            {chat.lastMessagePreview ? `Last: ${chat.lastMessagePreview}` : 'No messages yet'}
                          </p>
                          <div className="mt-3 flex items-center justify-between text-xs text-white/70">
                            <span>{lastMessageLabel}</span>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                setQrChat({ ...chat, link: chatLink });
                              }}
                              className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-1 text-white hover:bg-white/25"
                            >
                              <FiLink className="h-3 w-3" />
                              QR
                            </button>
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="mt-8">
          <div className="card">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Bookmarks</h3>
                <p className="text-sm text-gray-500">Synced from the FutureNote Edge extension. Drag folders and icons to reorder.</p>
              </div>
              <div className="text-right text-xs text-gray-500">
                <div>{activeBookmarkData?.totalCount || 0} bookmark{activeBookmarkData?.totalCount === 1 ? '' : 's'} loaded</div>
                {activeBookmarkData?.lastSyncedAt && (
                  <div>Last sync {new Date(activeBookmarkData.lastSyncedAt).toLocaleString()}</div>
                )}
              </div>
            </div>

            {hiddenBookmarkGroups.length > 0 && (
              <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-slate-800">Hidden folders</p>
                    <p className="text-xs text-slate-500">Hidden state is saved locally for this user and browser.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setHiddenBookmarkFolders((prev) => {
                        const next = { ...prev };
                        for (const group of hiddenBookmarkGroups) {
                          next[group.folderPath] = false;
                        }
                        return next;
                      });
                    }}
                    className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                  >
                    Show all hidden
                  </button>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {hiddenBookmarkGroups.map((group) => (
                    <button
                      key={group.folderPath}
                      type="button"
                      onClick={() => setHiddenBookmarkFolders((prev) => ({
                        ...prev,
                        [group.folderPath]: false
                      }))}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:border-primary-200 hover:text-primary-700"
                    >
                      <FiEye className="h-3.5 w-3.5" />
                      <span>{group.folderPath}</span>
                      <span className="text-[10px] text-slate-400">{group.items.length}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {bookmarksLoading && !activeBookmarkData ? (
              <div className="flex justify-center py-6">
                <LoadingSpinner />
              </div>
            ) : bookmarkGroups.length === 0 && hiddenBookmarkGroups.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-500">
                No bookmarks synced yet. Load the Edge extension, sign in with your FutureNote account, and run a sync.
              </div>
            ) : bookmarkGroups.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-600">
                All bookmark folders are currently hidden. Use the hidden folders panel above to show them again.
              </div>
            ) : (
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: `repeat(auto-fit, minmax(${BOOKMARK_GRID_CELL_SIZE}px, ${BOOKMARK_GRID_CELL_SIZE}px))`,
                  gridAutoRows: `${BOOKMARK_GRID_CELL_SIZE}px`,
                  gridAutoFlow: 'dense',
                  justifyContent: 'center'
                }}
              >
                {bookmarkGroups.map((group) => {
                  const isCollapsed = !!collapsedBookmarkFolders[group.folderPath];
                  const tileMeta = getBookmarkTileMeta(group.items.length, isCollapsed);
                  const visibleBookmarks = isCollapsed
                    ? group.items.slice(0, tileMeta.previewCount)
                    : group.items;
                  const hiddenCount = Math.max(0, group.items.length - visibleBookmarks.length);

                  return (
                    <div
                      key={group.folderPath}
                      draggable
                      onDragStart={() => setBookmarkDrag({ type: 'folder', folderPath: group.folderPath })}
                      onDragEnd={() => setBookmarkDrag(null)}
                      onDragOver={(event) => {
                        if (bookmarkDrag?.type === 'folder') {
                          event.preventDefault();
                        }
                      }}
                      onDrop={(event) => {
                        if (bookmarkDrag?.type === 'folder') {
                          event.preventDefault();
                          handleFolderDrop(group.folderPath);
                        }
                      }}
                      className={`overflow-hidden rounded-2xl border bg-white shadow-sm transition ${
                        bookmarkDrag?.type === 'folder' && bookmarkDrag.folderPath === group.folderPath
                          ? 'border-primary-300 ring-2 ring-primary-100'
                          : 'border-gray-200'
                      }`}
                      style={{
                        gridColumnEnd: `span ${tileMeta.colSpan}`,
                        gridRowEnd: `span ${tileMeta.rowSpan}`
                      }}
                    >
                      <div className={`flex h-full flex-col ${isCollapsed ? 'p-3' : 'p-4'}`}>
                        <div className="flex items-start justify-between gap-3">
                          <button
                            type="button"
                            onClick={() => setCollapsedBookmarkFolders((prev) => ({
                              ...prev,
                              [group.folderPath]: !prev[group.folderPath]
                            }))}
                            className="flex min-w-0 items-center gap-2 text-left text-gray-900"
                          >
                            {isCollapsed ? (
                              <FiChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
                            ) : (
                              <FiChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" />
                            )}
                            <FiFolder className={`${isCollapsed ? 'h-3.5 w-3.5' : 'h-4 w-4'} flex-shrink-0 text-amber-500`} />
                            <h4 className={`truncate font-semibold ${isCollapsed ? 'text-xs' : 'text-sm'}`}>{group.folderPath}</h4>
                          </button>
                          <div className="flex items-center gap-2">
                            <span className={`rounded-full bg-gray-100 text-gray-500 ${isCollapsed ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-[11px] uppercase tracking-wide'}`}>
                              {group.items.length}
                            </span>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                setHiddenBookmarkFolders((prev) => ({
                                  ...prev,
                                  [group.folderPath]: true
                                }));
                              }}
                              className="rounded-full border border-gray-200 bg-white p-1.5 text-gray-400 transition hover:border-gray-300 hover:text-gray-600"
                              title="Hide folder"
                            >
                              <FiEyeOff className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>

                        <div
                          className={`mt-3 flex-1 ${isCollapsed ? 'overflow-hidden' : 'overflow-y-auto pr-1'}`}
                          onDragOver={(event) => {
                            if (bookmarkDrag?.type === 'bookmark' && bookmarkDrag.folderPath === group.folderPath) {
                              event.preventDefault();
                            }
                          }}
                          onDrop={(event) => {
                            if (bookmarkDrag?.type === 'bookmark' && bookmarkDrag.folderPath === group.folderPath) {
                              event.preventDefault();
                              handleBookmarkGroupDropToEnd(group.folderPath);
                            }
                          }}
                        >
                          <div
                            className="grid gap-2"
                            style={{
                              gridTemplateColumns: `repeat(${tileMeta.columns}, minmax(0, 1fr))`
                            }}
                          >
                            {visibleBookmarks.map((bookmark) => {
                              const iconUrl = getBookmarkDisplayIcon(bookmark);
                              const hasIconError = bookmarkIconErrors[bookmark.id];

                              return (
                                <a
                                  key={bookmark.id}
                                  href={bookmark.url}
                                  target={getEffectiveBookmarkOpenMode(bookmark, bookmarkFolderOverrideMap, bookmarkDefaultOpenMode) === 'new_tab' ? '_blank' : undefined}
                                  rel={getEffectiveBookmarkOpenMode(bookmark, bookmarkFolderOverrideMap, bookmarkDefaultOpenMode) === 'new_tab' ? 'noreferrer' : undefined}
                                  draggable
                                  onDragStart={() => setBookmarkDrag({
                                    type: 'bookmark',
                                    folderPath: group.folderPath,
                                    bookmarkId: bookmark.id
                                  })}
                                  onDragEnd={() => setBookmarkDrag(null)}
                                  onDragOver={(event) => {
                                    if (bookmarkDrag?.type === 'bookmark' && bookmarkDrag.folderPath === group.folderPath) {
                                      event.preventDefault();
                                      event.stopPropagation();
                                    }
                                  }}
                                  onDrop={(event) => {
                                    if (bookmarkDrag?.type === 'bookmark' && bookmarkDrag.folderPath === group.folderPath) {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      handleBookmarkDrop(group.folderPath, bookmark.id);
                                    }
                                  }}
                                  className={`group flex flex-col items-center rounded-xl border px-2 py-2 text-center transition ${
                                    isCollapsed ? 'h-14 justify-center' : 'h-[70px] justify-start'
                                  } ${
                                    bookmarkDrag?.type === 'bookmark' && bookmarkDrag.bookmarkId === bookmark.id
                                      ? 'border-primary-300 bg-primary-50'
                                      : 'border-gray-100 bg-gray-50 hover:border-primary-200 hover:bg-primary-50'
                                  }`}
                                  title={bookmark.title}
                                >
                                  <span className={`flex items-center justify-center overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-200 ${
                                    isCollapsed ? 'h-7 w-7' : 'h-9 w-9'
                                  }`}>
                                    {!hasIconError && iconUrl ? (
                                      <img
                                        src={iconUrl}
                                        alt=""
                                        className={isCollapsed ? 'h-[18px] w-[18px] object-contain' : 'h-6 w-6 object-contain'}
                                        onError={() => setBookmarkIconErrors((prev) => ({ ...prev, [bookmark.id]: true }))}
                                      />
                                    ) : (
                                      <span className="text-[10px] font-semibold text-gray-500">
                                        {getBookmarkInitials(bookmark.title, bookmark.url)}
                                      </span>
                                    )}
                                  </span>
                                  {!isCollapsed && (
                                    <span className="mt-2 line-clamp-2 text-[11px] font-medium leading-4 text-gray-700">
                                      {bookmark.title}
                                    </span>
                                  )}
                                </a>
                              );
                            })}
                          </div>

                          {hiddenCount > 0 && (
                            <div className={`text-center font-medium text-gray-400 ${isCollapsed ? 'mt-2 text-[10px]' : 'mt-3 text-[11px]'}`}>
                              + {hiddenCount} more bookmark{hiddenCount === 1 ? '' : 's'}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="mt-8">
          <div className="card">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">FutureNotes</h3>
                <p className="text-sm text-gray-500">Publish updates for all logged-in users.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3 text-sm font-medium text-gray-900">
                  <FiEdit className="h-4 w-4 text-gray-400" />
                  Publish new note
                </div>
                <textarea
                  value={noteContent}
                  onChange={(event) => setNoteContent(event.target.value)}
                  className="input-field h-32"
                  placeholder="Write your note in Markdown..."
                  maxLength={5000}
                />
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <input
                    type="datetime-local"
                    value={notePublishAt}
                    onChange={(event) => setNotePublishAt(event.target.value)}
                    className="input-field"
                  />
                  <button
                    type="button"
                    onClick={handleCreateNote}
                    className="btn-primary"
                    disabled={createNoteMutation.isLoading}
                  >
                    {createNoteMutation.isLoading ? 'Publishing...' : 'Publish'}
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">Notes appear for everyone after the publish time.</p>
              </div>

              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <p className="text-xs font-semibold text-gray-500 mb-2">Preview</p>
                {noteContent.trim() ? (
                  <div
                    className="text-sm text-gray-700 space-y-2"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(noteContent) }}
                  />
                ) : (
                  <p className="text-sm text-gray-400">Start typing to see a preview.</p>
                )}
              </div>
            </div>

            <div className="mt-6">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Latest notes</h4>
              {notesLoading ? (
                <div className="flex justify-center py-6">
                  <LoadingSpinner />
                </div>
              ) : notes.length === 0 ? (
                <p className="text-sm text-gray-500">No published notes yet.</p>
              ) : (
                <div className="columns-1 sm:columns-2 lg:columns-3 gap-4">
                  {notes.map((note) => (
                    <div key={note.id} className="note-tile mb-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                      <div
                        className="text-sm text-gray-700 space-y-2"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(note.content) }}
                      />
                      <div className="mt-3 text-xs text-gray-400">
                        {new Date(note.publishAt).toLocaleString()}
                        {note.author?.firstName && (
                          <> · {note.author.firstName} {note.author.lastName}</>
                        )}
                      </div>

                      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                        <button
                          type="button"
                          className={`px-2 py-1 rounded-full border ${note.userReaction === 'like' ? 'border-primary-500 text-primary-600' : 'border-gray-200'}`}
                          onClick={() => reactionMutation.mutate({
                            noteId: note.id,
                            type: note.userReaction === 'like' ? 'none' : 'like'
                          })}
                        >
                          Like · {note.likesCount || 0}
                        </button>
                        <button
                          type="button"
                          className={`px-2 py-1 rounded-full border ${note.userReaction === 'dislike' ? 'border-red-500 text-red-600' : 'border-gray-200'}`}
                          onClick={() => reactionMutation.mutate({
                            noteId: note.id,
                            type: note.userReaction === 'dislike' ? 'none' : 'dislike'
                          })}
                        >
                          Dislike · {note.dislikesCount || 0}
                        </button>
                        <button
                          type="button"
                          className="px-2 py-1 rounded-full border border-gray-200"
                          onClick={() => setExpandedNoteId((prev) => (prev === note.id ? null : note.id))}
                        >
                          Comments · {note.commentsCount || 0}
                        </button>
                      </div>

                      {expandedNoteId === note.id && (
                        <div className="mt-4 space-y-3">
                          {commentsLoading ? (
                            <div className="text-xs text-gray-400">Loading comments...</div>
                          ) : noteComments.length === 0 ? (
                            <div className="text-xs text-gray-400">No comments yet.</div>
                          ) : (
                            <div className="space-y-2">
                              {noteComments.map((comment) => (
                                <div key={comment.id} className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-700">
                                  <div className="font-semibold">
                                    {comment.author?.firstName} {comment.author?.lastName}
                                  </div>
                                  <div className="mt-1">{comment.content}</div>
                                  <div className="mt-1 text-[10px] text-gray-400">
                                    {new Date(comment.createdAt).toLocaleString()}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="flex items-center gap-2">
                            <input
                              value={commentDrafts[note.id] || ''}
                              onChange={(event) =>
                                setCommentDrafts((prev) => ({ ...prev, [note.id]: event.target.value }))
                              }
                              className="input-field text-sm"
                              placeholder="Add a comment"
                            />
                            <button
                              type="button"
                              className="btn-secondary text-sm"
                              onClick={() => {
                                const content = (commentDrafts[note.id] || '').trim();
                                if (!content) {
                                  toast.error('Write a comment first');
                                  return;
                                }
                                commentMutation.mutate({ noteId: note.id, content });
                              }}
                              disabled={commentMutation.isLoading}
                            >
                              Post
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        </>
        )}
      </div>

      {isCreateChatOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 relative">
            <button
              type="button"
              onClick={() => setIsCreateChatOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <FiX className="h-5 w-5" />
            </button>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Create a new chat</h3>
            <p className="text-sm text-gray-500 mb-4">Add a title and optional background image.</p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Chat title</label>
                <input
                  value={chatTitle}
                  onChange={(event) => setChatTitle(event.target.value)}
                  className="input-field"
                  placeholder="e.g. Investor Q&A"
                  maxLength={80}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Background image</label>
                <div className="flex items-center gap-3">
                  <label className="btn-secondary cursor-pointer flex items-center">
                    <FiImage className="h-4 w-4 mr-2" />
                    Choose image
                    <input type="file" accept="image/*" onChange={handleBackgroundSelect} className="hidden" />
                  </label>
                  {isProcessingBackground && <span className="text-xs text-gray-500">Processing...</span>}
                </div>
                {chatBackground && (
                  <div className="mt-3 rounded-lg overflow-hidden border border-gray-200">
                    <img src={chatBackground} alt="Chat background preview" className="h-32 w-full object-cover" />
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button type="button" className="btn-secondary" onClick={() => setIsCreateChatOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleCreateChat}
                disabled={createChatMutation.isLoading || isProcessingBackground}
              >
                {createChatMutation.isLoading ? 'Creating...' : 'Create chat'}
              </button>
            </div>
          </div>
        </div>
      )}

      {qrChat && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 relative">
            <button
              type="button"
              onClick={() => setQrChat(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <FiX className="h-5 w-5" />
            </button>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Guest chat link</h3>
            <p className="text-sm text-gray-500 mb-4">Scan or share this QR code to join as a guest.</p>

            <div className="flex justify-center mb-4">
              <QRCodeCanvas value={qrChat.link} size={200} />
            </div>

            <div className="space-y-2">
              <input
                readOnly
                value={qrChat.link}
                className="input-field text-xs"
              />
              <button
                type="button"
                className="btn-secondary w-full flex items-center justify-center"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(qrChat.link);
                    toast.success('Link copied');
                  } catch (error) {
                    toast.error('Failed to copy link');
                  }
                }}
              >
                <FiLink className="h-4 w-4 mr-2" />
                Copy link
              </button>
            </div>
          </div>
        </div>
      )}

      <TasksDrawer
        open={isTasksDrawerOpen || isTasksDrawerPinned}
        pinned={isTasksDrawerPinned}
        onPinnedChange={handleTasksDrawerPinnedChange}
        onClose={handleCloseTasksDrawer}
      />
    </div>
  );
};

export default Dashboard;


